// ============================================
// CHÁMOMILE — Member Dashboard
// Points, tier, order history, engagement timeline, and notifications
// ============================================

import React, { useState, useEffect } from 'react';
import { collection, query, where, orderBy, getDocs, limit } from 'firebase/firestore';
import { db } from '../config/firebase';
import {
    getMemberTier, getNextTier, getNextReward,
    REWARDS, TIERS, REWARDS_UI_ENABLED
} from '../services/loyaltyService';
import { Clock, CheckCircle2, Coffee, Package, XCircle } from 'lucide-react';

// Premium Gold Color Palette (matches ChamomileApp)
const colors = {
    gold50: '#faf8f3', gold100: '#f5f0e6', gold200: '#ebe0cc', gold300: '#dccaa6',
    gold400: '#c9a962', gold500: '#b8943d', gold600: '#a07d2e', gold700: '#846628',
    stone50: '#fafaf9', stone100: '#f5f5f4', stone200: '#e7e5e4', stone300: '#d6d3d1',
    stone400: '#a8a29e', stone500: '#78716c', stone600: '#57534e', stone700: '#44403c',
    stone800: '#292524', stone900: '#1c1917',
    success: '#22c55e', error: '#ef4444',
};

// Tabs for the dashboard
const TABS = [
    { id: 'overview', label: 'Overview', icon: '✨' },
    { id: 'orders', label: 'Orders', icon: '📦' },
    { id: 'timeline', label: 'Timeline', icon: '📅' },
];

const MemberDashboard = ({ memberData, currentUser, onClose }) => {
    const [activeTab, setActiveTab] = useState('overview');
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(false);

    if (!memberData) return null;

    const [dynamicStats, setDynamicStats] = useState({
        totalSpent: memberData?.totalSpent || 0,
        totalOrders: memberData?.totalOrders || 0
    });

    const points = memberData.loyaltyPoints || 0;
    const currentTier = getMemberTier(points);
    const nextTierInfo = getNextTier(points);
    const nextReward = getNextReward(points);
    const totalSpent = dynamicStats.totalSpent;
    const totalOrders = dynamicStats.totalOrders;
    const memberSince = memberData.createdAt?.toDate?.() || memberData.memberSince?.toDate?.() || null;

    // Tier progress percentage
    const tierProgressPercent = (() => {
        if (points >= 1000) return 100;
        if (points >= 500) return ((points - 500) / 500) * 100;
        if (points >= 100) return ((points - 100) / 400) * 100;
        return (points / 100) * 100;
    })();


    // Load order history on mount
    useEffect(() => {
        loadOrders();
    }, []); // eslint-disable-line


    const loadOrders = async () => {
        if (orders.length > 0 || loadingOrders) return;
        setLoadingOrders(true);
        try {
            const email = currentUser?.email || memberData.email;
            const phone = memberData.phone ? memberData.phone.replace(/\D/g, '') : (memberData.id || '').replace(/\D/g, '');
            const memberId = memberData.id;

            if (!email && !phone && !memberId) { setLoadingOrders(false); return; }

            const onlineOrdersRef = collection(db, 'online-orders');
            const posOrdersRef = collection(db, 'orders');

            let allOrders = [];

            // 1. Fetch Online Orders (by email or phone)
            let onlineResults = [];
            if (email) {
                const qEmail = query(onlineOrdersRef, where('customerInfo.email', '==', email), limit(50));
                const snapEmail = await getDocs(qEmail);
                onlineResults = snapEmail.docs.map(d => ({ id: d.id, _source: 'online', ...d.data() }));
            }
            if (phone) {
                const qPhone = query(onlineOrdersRef, where('customerInfo.phone', '==', phone), limit(50));
                const snapPhone = await getDocs(qPhone);
                snapPhone.docs.forEach(d => {
                    if (!onlineResults.find(o => o.id === d.id)) {
                        onlineResults.push({ id: d.id, _source: 'online', ...d.data() });
                    }
                });
            }
            allOrders = [...onlineResults];

            // 2. Fetch POS Orders (by customerId, phone, or memberId)
            let posResults = [];

            // Query 1: by customerId (exact match of member ID)
            if (memberId) {
                const qPos1 = query(posOrdersRef, where('customerId', '==', memberId), limit(50));
                const snapPos1 = await getDocs(qPos1);
                snapPos1.docs.forEach(d => posResults.push({ id: d.id, _source: 'pos', ...d.data() }));
            }

            // Query 2: by customerPhone (if phone exists)
            if (phone) {
                // Check customerPhone field
                const qPos2 = query(posOrdersRef, where('customerPhone', '==', phone), limit(50));
                const snapPos2 = await getDocs(qPos2);
                snapPos2.docs.forEach(d => {
                    if (!posResults.find(p => p.id === d.id)) posResults.push({ id: d.id, _source: 'pos', ...d.data() });
                });

                // Also check phone field in case it's used instead
                const qPos3 = query(posOrdersRef, where('phone', '==', phone), limit(50));
                const snapPos3 = await getDocs(qPos3);
                snapPos3.docs.forEach(d => {
                    if (!posResults.find(p => p.id === d.id)) posResults.push({ id: d.id, _source: 'pos', ...d.data() });
                });

                // Check generic customer field (POS sometimes stores phone here directly)
                const qPos4 = query(posOrdersRef, where('customer', '==', phone), limit(50));
                const snapPos4 = await getDocs(qPos4);
                snapPos4.docs.forEach(d => {
                    if (!posResults.find(p => p.id === d.id)) posResults.push({ id: d.id, _source: 'pos', ...d.data() });
                });
            }

            // Query 3: by member name matching the POS generic 'customer' field
            if (memberData.name) {
                const qPosName = query(posOrdersRef, where('customer', '==', memberData.name), limit(50));
                const snapPosName = await getDocs(qPosName);
                snapPosName.docs.forEach(d => {
                    if (!posResults.find(p => p.id === d.id)) posResults.push({ id: d.id, _source: 'pos', ...d.data() });
                });
            }

            allOrders = [...allOrders, ...posResults];

            // Filter out cancelled orders
            allOrders = allOrders.filter(o => o.status !== 'cancelled');

            // Sort newest first
            allOrders.sort((a, b) => {
                const dateA = a.createdAt?.toDate?.() || new Date(a.createdAt || a.time || 0);
                const dateB = b.createdAt?.toDate?.() || new Date(b.createdAt || b.time || 0);
                return dateB - dateA;
            });

            // Calculate exact total spent based on actual valid orders
            const calculatedSpent = allOrders.reduce((sum, order) => sum + (order.total || 0), 0);

            setDynamicStats({
                totalSpent: calculatedSpent,
                totalOrders: allOrders.length
            });

            setOrders(allOrders);
        } catch (err) {
            console.error('[MemberDashboard] Error loading orders:', err);
        }
        setLoadingOrders(false);
    };

    // Build timeline events from orders + member data
    const buildTimeline = () => {
        const events = [];

        // Registration event
        if (memberSince) {
            events.push({
                date: memberSince,
                type: 'register',
                icon: '🎉',
                title: 'Joined Chámomile',
                description: `Signed up as a ${TIERS[0].icon} ${TIERS[0].name} member`,
            });
        }

        // Order events + points earned
        orders.forEach(order => {
            const date = order.createdAt?.toDate?.() || (order.time ? new Date(order.time) : (order.date ? new Date(order.date) : null));
            if (!date || isNaN(date)) return;

            events.push({
                date,
                type: 'order',
                icon: '🛒',
                title: `Order #${order.orderNumber || order.orderId?.slice(-6) || ''}`,
                description: `${order.totalBottles || 0} bottles • RM ${(order.total || 0).toFixed(2)}`,
                detail: (() => {
                    const itemsArr = Array.isArray(order.items) ? order.items : (Array.isArray(order.products) ? order.products : null);
                    return itemsArr ? itemsArr.map(i => `${i.productName || i.name} ×${i.quantity}`).join(', ') : '';
                })(),
            });

            // Points earned event
            const earned = order._source === 'online' ? 0 : Math.floor(order.total || 0);
            if (earned > 0) {
                events.push({
                    date: new Date(date.getTime() + 1000), // 1s after order
                    type: 'points',
                    icon: '⭐',
                    title: `+${earned} points earned`,
                    description: `From order #${order.orderNumber || ''}`,
                });
            }
        });

        // Tier milestone events based on totalSpent thresholds
        const tierThresholds = [
            { spent: 100, tier: TIERS[1] },
            { spent: 500, tier: TIERS[2] },
            { spent: 1000, tier: TIERS[3] },
        ];
        tierThresholds.forEach(t => {
            if (totalSpent >= t.spent) {
                events.push({
                    date: new Date(), // Approximate — we don't track exact tier upgrade dates
                    type: 'tier',
                    icon: t.tier.icon,
                    title: `Reached ${t.tier.name}`,
                    description: `Unlocked after spending RM ${t.spent}+`,
                    approximate: true,
                });
            }
        });

        // Sort newest first
        return events.sort((a, b) => b.date - a.date);
    };

    const getTierPerks = () => {
        const perks = [
            { tier: 'Tea Leaf', perks: ['1 pt/RM 1 (Walk-in only, no points for Online)', 'View order history', 'Track your rewards'] },
            { tier: 'Blossom', perks: ['1 pt/RM 1 (Walk-in only, no points for Online)', 'View order history', 'Track your rewards', 'Member-only updates'] },
            { tier: 'Royal Blend', perks: ['1 pt/RM 1 (Walk-in only, no points for Online)', 'View order history', 'Track your rewards', 'Member-only updates', 'Early access to new flavours 🌟', 'Priority collection'] },
            { tier: 'Grand Cru', perks: ['1 pt/RM 1 (Walk-in only, no points for Online)', 'View order history', 'Track your rewards', 'Member-only updates', 'Early access to new flavours 🌟', 'Priority collection', '10% off all merchandise'] },
        ];
        return perks;
    };

    const formatDate = (date) => {
        if (!date) return '';
        return date.toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    return (
        <div
            style={{
                position: 'fixed', inset: 0,
                background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)',
                zIndex: 200, display: 'flex', alignItems: 'center', justifyContent: 'center',
                padding: '16px',
            }}
            onClick={onClose}
        >
            <div
                style={{
                    background: '#fff', borderRadius: '24px', padding: '0',
                    maxWidth: '440px', width: '100%', maxHeight: '92vh',
                    overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Hero Header */}
                <div style={{
                    background: `linear-gradient(135deg, ${currentTier.color}, ${colors.gold600})`,
                    borderRadius: '24px 24px 0 0', padding: '28px 24px 20px',
                    color: '#fff', position: 'relative', overflow: 'hidden',
                }}>
                    <div style={{ position: 'absolute', top: '-20px', right: '-20px', width: '80px', height: '80px', borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                    <div style={{ position: 'absolute', bottom: '-10px', left: '20px', width: '40px', height: '40px', borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />

                    <button onClick={onClose} style={{
                        position: 'absolute', top: '20px', right: '24px',
                        background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: '50%',
                        width: '36px', height: '36px', cursor: 'pointer', color: '#fff',
                        fontSize: '22px', display: 'flex', alignItems: 'center', justifyContent: 'center',
                        transition: 'background 0.2s', zIndex: 10
                    }}>×</button>

                    {REWARDS_UI_ENABLED ? (
                        <>
                            <div style={{ fontSize: '13px', opacity: 0.9, marginBottom: '4px' }}>
                                {currentTier.icon} {currentTier.name}
                            </div>
                            <div style={{ fontSize: '42px', fontWeight: 700, letterSpacing: '-1px', lineHeight: 1.1 }}>
                                {points}
                            </div>
                            <div style={{ fontSize: '13px', opacity: 0.8, marginTop: '2px' }}>points available</div>

                            {nextTierInfo && (
                                <div style={{ marginTop: '14px' }}>
                                    <div style={{
                                        display: 'flex', justifyContent: 'space-between', fontSize: '11px', opacity: 0.8, marginBottom: '6px',
                                    }}>
                                        <span>{currentTier.name}</span>
                                        <span>{nextTierInfo.tier.icon} {nextTierInfo.tier.name} ({nextTierInfo.pointsNeeded} pts away)</span>
                                    </div>
                                    <div style={{ height: '6px', background: 'rgba(255,255,255,0.2)', borderRadius: '3px' }}>
                                        <div style={{
                                            height: '100%', width: `${tierProgressPercent}%`,
                                            background: '#fff', borderRadius: '3px',
                                            transition: 'width 0.5s ease',
                                        }} />
                                    </div>
                                </div>
                            )}
                            {!nextTierInfo && (
                                <div style={{ marginTop: '10px', fontSize: '12px', opacity: 0.8 }}>
                                    💎 Maximum tier achieved!
                                </div>
                            )}
                        </>
                    ) : (
                        <>
                            <div style={{ fontSize: '26px', fontWeight: 700, letterSpacing: '-0.5px', lineHeight: 1.2 }}>
                                {memberData.name || 'Welcome back'}
                            </div>
                            <div style={{ fontSize: '13px', opacity: 0.85, marginTop: '4px' }}>
                                {memberSince
                                    ? `Member since ${memberSince.toLocaleDateString('en-MY', { month: 'long', year: 'numeric' })}`
                                    : 'Chámomile member'}
                            </div>
                        </>
                    )}
                </div>

                {/* Member Notifications Banner */}
                {REWARDS_UI_ENABLED && (
                    <div style={{
                        padding: '12px 24px',
                        background: `linear-gradient(135deg, ${colors.gold50}, #fef3c7)`,
                        borderBottom: `1px solid ${colors.gold200}`,
                        fontSize: '12px', color: colors.gold700,
                        display: 'flex', alignItems: 'center', gap: '8px',
                    }}>
                        <span style={{ fontSize: '14px' }}>🔔</span>
                        <div>
                            {nextReward ? (
                                <span>You're <strong>{nextReward.pointsNeeded} pts</strong> away from <strong>{nextReward.label}</strong>! Redeem at any Chámomile outlet.</span>
                            ) : (
                                <span>You've unlocked all rewards! Visit any Chámomile outlet to redeem your points.</span>
                            )}
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <div style={{
                    display: 'flex', padding: '0 16px',
                    borderBottom: `1px solid ${colors.stone100}`,
                }}>
                    {TABS.map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            style={{
                                flex: 1, padding: '12px 8px', border: 'none', background: 'none',
                                fontSize: '12px', fontWeight: activeTab === tab.id ? 600 : 400,
                                color: activeTab === tab.id ? colors.gold600 : colors.stone400,
                                borderBottom: activeTab === tab.id ? `2px solid ${colors.gold500}` : '2px solid transparent',
                                cursor: 'pointer', fontFamily: 'inherit',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                            }}
                        >
                            <span>{tab.icon}</span> {tab.label}
                        </button>
                    ))}
                </div>

                {/* Tab Content */}
                <div style={{ padding: '16px 24px 24px' }}>

                    {/* ─── OVERVIEW TAB ─── */}
                    {activeTab === 'overview' && (
                        <>
                            {/* Stats */}
                            <div style={{
                                display: 'flex', gap: '12px', marginBottom: '16px',
                            }}>
                                <div style={{ flex: 1, textAlign: 'center', padding: '12px', background: colors.stone50, borderRadius: '12px' }}>
                                    <div style={{ fontSize: '18px', fontWeight: 700, color: colors.stone800 }}>{totalOrders}</div>
                                    <div style={{ fontSize: '11px', color: colors.stone400 }}>Orders</div>
                                </div>
                                <div style={{ flex: 1, textAlign: 'center', padding: '12px', background: colors.stone50, borderRadius: '12px' }}>
                                    <div style={{ fontSize: '18px', fontWeight: 700, color: colors.stone800 }}>RM {totalSpent.toFixed(0)}</div>
                                    <div style={{ fontSize: '11px', color: colors.stone400 }}>Total Spent</div>
                                </div>
                                {REWARDS_UI_ENABLED && (
                                    <div style={{ flex: 1, textAlign: 'center', padding: '12px', background: colors.gold50, borderRadius: '12px' }}>
                                        <div style={{ fontSize: '18px', fontWeight: 700, color: colors.gold500 }}>
                                            {nextReward ? nextReward.pointsNeeded : '✓'}
                                        </div>
                                        <div style={{ fontSize: '11px', color: colors.stone400 }}>
                                            {nextReward ? 'Pts to Reward' : 'All Unlocked'}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Next Reward Progress */}
                            {REWARDS_UI_ENABLED && nextReward && (
                                <div style={{ marginBottom: '16px', padding: '12px 14px', background: colors.stone50, borderRadius: '12px' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                        <span style={{ fontSize: '12px', fontWeight: 600, color: colors.stone700 }}>Next: {nextReward.label}</span>
                                        <span style={{ fontSize: '12px', color: colors.stone400 }}>{points}/{nextReward.points} pts</span>
                                    </div>
                                    <div style={{ height: '8px', background: colors.stone200, borderRadius: '4px' }}>
                                        <div style={{
                                            height: '100%', width: `${((nextReward.points - nextReward.pointsNeeded) / nextReward.points) * 100}%`,
                                            background: `linear-gradient(90deg, ${colors.gold300}, ${colors.gold500})`,
                                            borderRadius: '4px', transition: 'width 0.5s ease',
                                        }} />
                                    </div>
                                </div>
                            )}

                            {/* Rewards List (view-only — redeemable at POS) */}
                            {REWARDS_UI_ENABLED && (
                            <>
                            <h3 style={{ fontSize: '13px', fontWeight: 700, color: colors.stone700, margin: '0 0 10px' }}>
                                🎁 Rewards (redeem at any outlet)
                            </h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', marginBottom: '16px' }}>
                                {REWARDS.map(reward => {
                                    const isAvailable = points >= reward.points;
                                    return (
                                        <div key={reward.id} style={{
                                            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                                            padding: '10px 14px', borderRadius: '10px',
                                            background: isAvailable ? colors.gold50 : colors.stone50,
                                            border: `1px solid ${isAvailable ? colors.gold200 : colors.stone200}`,
                                            opacity: isAvailable ? 1 : 0.5,
                                        }}>
                                            <div>
                                                <div style={{ fontSize: '13px', fontWeight: 600, color: isAvailable ? colors.gold700 : colors.stone500 }}>
                                                    {reward.label}
                                                </div>
                                                <div style={{ fontSize: '11px', color: colors.stone400, marginTop: '2px' }}>
                                                    {reward.points} points
                                                </div>
                                            </div>
                                            {isAvailable ? (
                                                <span style={{
                                                    padding: '4px 10px', borderRadius: '999px', fontSize: '11px', fontWeight: 600,
                                                    background: colors.gold100, color: colors.gold600,
                                                }}>✓ Available</span>
                                            ) : (
                                                <span style={{ fontSize: '11px', color: colors.stone400 }}>
                                                    {reward.points - points} pts more
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Tier Guide */}
                            <h3 style={{ fontSize: '13px', fontWeight: 700, color: colors.stone700, margin: '0 0 10px' }}>
                                Membership Tiers
                            </h3>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '16px' }}>
                                {TIERS.map(tier => (
                                    <div key={tier.name} style={{
                                        display: 'flex', alignItems: 'center', gap: '10px',
                                        padding: '8px 12px', borderRadius: '8px',
                                        background: currentTier.name === tier.name ? colors.gold50 : 'transparent',
                                        border: currentTier.name === tier.name ? `1px solid ${colors.gold200}` : '1px solid transparent',
                                    }}>
                                        <span style={{ fontSize: '14px' }}>{tier.icon}</span>
                                        <span style={{
                                            flex: 1, fontSize: '12px',
                                            fontWeight: currentTier.name === tier.name ? 700 : 500,
                                            color: currentTier.name === tier.name ? colors.gold700 : colors.stone600,
                                        }}>{tier.name}</span>
                                        <span style={{ fontSize: '11px', color: colors.stone400 }}>
                                            {tier.minPoints === 0 ? 'Start' : `${tier.minPoints}+ pts`}
                                        </span>
                                        {currentTier.name === tier.name && (
                                            <span style={{
                                                fontSize: '10px', fontWeight: 600, color: colors.gold600,
                                                background: colors.gold100, padding: '2px 8px', borderRadius: '999px',
                                            }}>YOU</span>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Your Tier Perks */}
                            <h3 style={{ fontSize: '13px', fontWeight: 700, color: colors.stone700, margin: '0 0 10px' }}>
                                {currentTier.icon} Your {currentTier.name} Perks
                            </h3>
                            <div style={{
                                padding: '12px 16px', background: colors.gold50, borderRadius: '12px',
                                border: `1px solid ${colors.gold200}`,
                            }}>
                                {getTierPerks().find(t => t.tier === currentTier.name)?.perks.map((perk, i) => (
                                    <div key={i} style={{
                                        fontSize: '12px', color: colors.gold700, padding: '4px 0',
                                        display: 'flex', alignItems: 'center', gap: '6px',
                                    }}>
                                        <span style={{ color: colors.gold400 }}>✦</span> {perk}
                                    </div>
                                ))}
                            </div>
                            </>
                            )}

                            {/* Member Since */}
                            {memberSince && (
                                <div style={{
                                    marginTop: '16px', textAlign: 'center',
                                    fontSize: '11px', color: colors.stone400,
                                }}>
                                    Member since {formatDate(memberSince)}
                                </div>
                            )}
                        </>
                    )}

                    {/* ─── ORDERS TAB ─── */}
                    {activeTab === 'orders' && (
                        <>
                            <h3 style={{ fontSize: '14px', fontWeight: 700, color: colors.stone800, margin: '0 0 12px' }}>
                                Order History
                            </h3>
                            {loadingOrders ? (
                                <div style={{ textAlign: 'center', padding: '32px', color: colors.stone400 }}>
                                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
                                    Loading orders...
                                </div>
                            ) : orders.length === 0 ? (
                                <div style={{ textAlign: 'center', padding: '32px', color: colors.stone400 }}>
                                    <div style={{ fontSize: '32px', marginBottom: '8px', opacity: 0.5 }}>📦</div>
                                    <p style={{ margin: 0 }}>No orders found yet</p>
                                    <p style={{ margin: '4px 0 0', fontSize: '12px' }}>Your order history will appear here</p>
                                </div>
                            ) : (
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    {orders.map(order => {
                                        const date = order.createdAt?.toDate?.() || (order.time ? new Date(order.time) : (order.date ? new Date(order.date) : null));
                                        const statusColor = order.status === 'completed' ? colors.success
                                            : order.status === 'payment_uploaded' ? colors.gold500
                                                : colors.stone400;
                                        return (
                                            <div key={order.id} style={{
                                                padding: '14px', borderRadius: '12px',
                                                background: colors.stone50, border: `1px solid ${colors.stone200}`,
                                            }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                                                    <div>
                                                        <span style={{ fontSize: '14px', fontWeight: 700, color: colors.stone800 }}>
                                                            #{order.orderNumber || order.orderId?.slice(-6)}
                                                        </span>
                                                        <span style={{
                                                            fontSize: '10px', fontWeight: 600, marginLeft: '8px',
                                                            padding: '2px 8px', borderRadius: '999px',
                                                            background: `${statusColor}20`, color: statusColor,
                                                            textTransform: 'capitalize',
                                                        }}>
                                                            {(order.status || 'unknown').replace(/_/g, ' ')}
                                                        </span>
                                                    </div>
                                                    <span style={{ fontSize: '14px', fontWeight: 700, color: colors.gold600 }}>
                                                        RM {(order.total || 0).toFixed(2)}
                                                    </span>
                                                </div>
                                                <div style={{ fontSize: '12px', color: colors.stone500, marginBottom: '6px' }}>
                                                    {date ? formatDate(date) : ''} • {order.totalBottles || 0} bottles{order.deliveryMethod ? ` • ${order.deliveryMethod}` : ''}
                                                </div>
                                                {(Array.isArray(order.items) ? order.items : (Array.isArray(order.products) ? order.products : null)) && (
                                                    <div style={{ fontSize: '11px', color: colors.stone400 }}>
                                                        {(Array.isArray(order.items) ? order.items : (Array.isArray(order.products) ? order.products : [])).map(i => `${i.productName || i.name} ×${i.quantity}`).join(' • ')}
                                                    </div>
                                                )}
                                                {/* Points earned */}
                                                <div style={{
                                                    marginTop: '8px', paddingTop: '8px',
                                                    borderTop: `1px solid ${colors.stone200}`,
                                                    fontSize: '11px', color: colors.gold600, fontWeight: 600,
                                                    display: 'flex', alignItems: 'center', gap: '4px',
                                                }}>
                                                    ⭐ {order._source === 'online' ? '0' : `+${Math.floor(order.total || 0)}`} points earned
                                                </div>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </>
                    )}

                    {/* ─── TIMELINE TAB ─── */}
                    {activeTab === 'timeline' && (
                        <>
                            <h3 style={{ fontSize: '14px', fontWeight: 700, color: colors.stone800, margin: '0 0 12px' }}>
                                Your Journey
                            </h3>
                            {loadingOrders ? (
                                <div style={{ textAlign: 'center', padding: '32px', color: colors.stone400 }}>
                                    <div style={{ fontSize: '24px', marginBottom: '8px' }}>⏳</div>
                                    Loading timeline...
                                </div>
                            ) : (
                                <div style={{ position: 'relative', paddingLeft: '24px' }}>
                                    {/* Timeline line */}
                                    <div style={{
                                        position: 'absolute', left: '7px', top: '8px', bottom: '8px',
                                        width: '2px', background: colors.stone200,
                                    }} />

                                    {buildTimeline().map((event, idx) => (
                                        <div key={idx} style={{
                                            position: 'relative', paddingBottom: '16px',
                                        }}>
                                            {/* Timeline dot */}
                                            <div style={{
                                                position: 'absolute', left: '-20px', top: '2px',
                                                width: '16px', height: '16px', borderRadius: '50%',
                                                background: event.type === 'tier' ? colors.gold400
                                                    : event.type === 'register' ? colors.success
                                                        : '#fff',
                                                border: `2px solid ${event.type === 'tier' ? colors.gold400 : event.type === 'register' ? colors.success : colors.stone300}`,
                                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                fontSize: '8px', zIndex: 1,
                                            }}>
                                                {event.type === 'tier' || event.type === 'register' ? '' : ''}
                                            </div>

                                            {/* Event content */}
                                            <div style={{
                                                padding: '8px 12px', borderRadius: '10px',
                                                background: event.type === 'tier' ? colors.gold50
                                                    : event.type === 'register' ? '#f0fdf4'
                                                        : colors.stone50,
                                                border: `1px solid ${event.type === 'tier' ? colors.gold200
                                                    : event.type === 'register' ? '#bbf7d0'
                                                        : colors.stone200}`,
                                            }}>
                                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <span style={{ fontSize: '12px', fontWeight: 600, color: colors.stone700 }}>
                                                        {event.icon} {event.title}
                                                    </span>
                                                    {!event.approximate && (
                                                        <span style={{ fontSize: '10px', color: colors.stone400 }}>
                                                            {formatDate(event.date)}
                                                        </span>
                                                    )}
                                                </div>
                                                <div style={{ fontSize: '11px', color: colors.stone500, marginTop: '2px' }}>
                                                    {event.description}
                                                </div>
                                                {event.detail && (
                                                    <div style={{ fontSize: '10px', color: colors.stone400, marginTop: '4px' }}>
                                                        {event.detail}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    ))}

                                    {buildTimeline().length === 0 && (
                                        <div style={{ textAlign: 'center', padding: '24px', color: colors.stone400 }}>
                                            <p style={{ fontSize: '13px' }}>Your journey starts here!</p>
                                            <p style={{ fontSize: '11px', margin: '4px 0 0' }}>Place your first order to see your timeline</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                </div>

                {/* Footer — How It Works */}
                {REWARDS_UI_ENABLED && (
                <div style={{
                    padding: '12px 24px 20px', margin: '0 16px 16px',
                    background: colors.stone50, borderRadius: '12px',
                    fontSize: '12px', color: colors.stone500, lineHeight: 1.6,
                }}>
                    <strong style={{ color: colors.stone700 }}>How It Works & Earning Rules</strong>
                    <div style={{ marginTop: '6px' }}>
                        ⭐ <strong>Walk-in:</strong> 1 pt per RM 1 spent<br />
                        ⭐ <strong>Online Orders:</strong> 0 pts (ALL orders from Online shall not calculate points)<br />
                        🎁 Redeem rewards <strong>at any physical outlet or pop-up</strong> by providing your number.<br /><br />
                        <em>Note: Online bulk orders automatically receive separate volume discounts instead of loyalty point redemptions.</em>
                    </div>

                    <strong style={{ color: colors.stone700, display: 'block', marginTop: '16px' }}>Terms & Conditions</strong>
                    <ol style={{ marginTop: '6px', paddingLeft: '20px', fontSize: '12px', color: colors.stone500, lineHeight: 1.5, margin: '6px 0 0 0' }}>
                        <li>Reward redemption is applicable only on the next purchase.</li>
                        <li>Rewards are strictly non‑transferable to other parties.</li>
                        <li>Rewards cannot be exchanged for cash or credit.</li>
                        <li>Rewards cannot be combined with other ongoing promotions.</li>
                        <li>Only one reward to be claimed per visit. Even if a member has sufficient points for multiple rewards, only one reward redemption is allowed per visit.</li>
                        <li>The management reserves the right to amend these terms and conditions at any time without prior notice.</li>
                    </ol>
                </div>
                )}
            </div>
        </div>
    );
};

export default MemberDashboard;
